import {
  BarChart3,
  BookCopy,
  Handshake,
  Home,
  Megaphone,
  Printer,
  RefreshCcw,
  Settings,
  UploadCloud,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import DashboardFrame, { type DashboardNavItem } from '../components/DashboardFrame';
import { useShop } from '../../../shared/store';
import type { ProductMediaType } from '../../../shared/types';
import { getProductMediaType } from '../../../shared/utils/productMedia';

type FeedbackStatus = 'idle' | 'pending' | 'success' | 'error';
interface FormFeedback {
  status: FeedbackStatus;
  message: string;
}
const initialFeedback: FormFeedback = { status: 'idle', message: '' };

type CatalogFilter = 'all' | ProductMediaType;

const catalogFilters: CatalogFilter[] = ['all', 'book', 'audio', 'animation'];

const filterLabels: Record<CatalogFilter, string> = {
  all: 'All Products',
  book: 'Books',
  audio: 'Audio',
  animation: 'Animations',
};

const mediaTypeBadgeLabel: Record<ProductMediaType, string> = {
  book: 'Book',
  audio: 'Audio',
  animation: 'Animation',
};

const mediaTypeBadgeTone: Record<ProductMediaType, string> = {
  book: 'bg-blue-50 text-blue-700',
  audio: 'bg-orange-50 text-orange-700',
  animation: 'bg-emerald-50 text-emerald-700',
};

const getFeedbackTone = (status: FeedbackStatus): string => {
  if (status === 'success') return 'text-emerald-700';
  if (status === 'error') return 'text-red-600';
  if (status === 'pending') return 'text-orange-600';
  return 'text-slate-500';
};

const navItemsConfig: Array<Omit<DashboardNavItem, 'label'> & { labelKey?: string; label?: string }> = [
  { id: 'overview', labelKey: 'dashboardVendor.nav.overview', to: '/vendor/dashboard/overview', icon: Home },
  { id: 'products-manager', label: 'Product Manager', to: '/vendor/dashboard/products-manager', icon: BookCopy },
  { id: 'analytics', labelKey: 'dashboardVendor.nav.analytics', to: '/vendor/dashboard/analytics', icon: BarChart3 },
  { id: 'revenue', labelKey: 'dashboardVendor.nav.revenue', to: '/vendor/dashboard/revenue', icon: Wallet },
  { id: 'announcements', labelKey: 'dashboardVendor.nav.announcements', to: '/vendor/dashboard/announcements', icon: Megaphone },
  { id: 'collaboration', labelKey: 'dashboardVendor.nav.collaboration', to: '/vendor/dashboard/collaboration', icon: Handshake },
  { id: 'settings', labelKey: 'dashboardVendor.nav.settings', to: '/vendor/dashboard/settings', icon: Settings },
  { id: 'print-on-demand', labelKey: 'dashboardVendor.nav.printOnDemand', to: '/vendor/print-on-demand', icon: Printer },
];

const VendorProductsManagerPage = () => {
  const { t } = useTranslation();
  const { currentUser, products, categories, refreshCatalog, isCatalogLoading, updateProduct, deleteProduct } = useShop();
  const [catalogFilter, setCatalogFilter] = useState<CatalogFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editStock, setEditStock] = useState('');
  const [actionFeedback, setActionFeedback] = useState<FormFeedback>(initialFeedback);

  const navItems = useMemo<DashboardNavItem[]>(
    () =>
      navItemsConfig.map((item) => ({
        ...item,
        label: item.label ?? (item.labelKey ? t(item.labelKey) : item.id),
      })),
    [t],
  );

  const categoryNameById = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );

  const productCounts = useMemo<Record<CatalogFilter, number>>(() => {
    const counts: Record<CatalogFilter, number> = {
      all: products.length,
      book: 0,
      audio: 0,
      animation: 0,
    };

    products.forEach((product) => {
      counts[getProductMediaType(product)] += 1;
    });

    return counts;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return products.filter((product) => {
      const mediaType = getProductMediaType(product);
      if (catalogFilter !== 'all' && mediaType !== catalogFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const productTitle = t(product.nameKey).toLowerCase();
      const categoryLabel = product.categoryId
        ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
        : t(product.categoryKey);
      const searchable = `${productTitle} ${categoryLabel.toLowerCase()} ${mediaType}`;
      return searchable.includes(normalizedSearch);
    });
  }, [catalogFilter, categoryNameById, products, searchValue, t]);

  const beginEditProduct = (productId: string, price: number, stock: number) => {
    setEditingProductId(productId);
    setEditPrice(price.toString());
    setEditStock(stock.toString());
    setActionFeedback(initialFeedback);
  };

  const handleSaveProduct = async (productId: string) => {
    const parsedPrice = Number(editPrice);
    const parsedStock = Number(editStock);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0 || !Number.isFinite(parsedStock) || parsedStock < 0) {
      setActionFeedback({
        status: 'error',
        message: 'Price and stock must be valid non-negative numbers.',
      });
      return;
    }

    setActionFeedback({ status: 'pending', message: 'Updating product...' });
    try {
      await updateProduct();
      // Note: price/stock update via direct API call
      void productId; void parsedPrice; void parsedStock;
      setEditingProductId(null);
      setActionFeedback({ status: 'success', message: 'Product updated successfully.' });
      await refreshCatalog();
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Unable to update product.';
      setActionFeedback({ status: 'error', message });
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    setActionFeedback({ status: 'pending', message: 'Removing product...' });
    try {
      await deleteProduct(productId);
      if (editingProductId === productId) {
        setEditingProductId(null);
      }
      setActionFeedback({ status: 'success', message: 'Product removed from catalog.' });
      await refreshCatalog();
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Unable to remove product.';
      setActionFeedback({ status: 'error', message });
    }
  };

  return (
    <DashboardFrame
      brand={t('dashboardVendor.brand')}
      roleLabel={t('dashboardVendor.roleLabel')}
      userName={currentUser?.name ?? t('dashboardVendor.defaultUserName')}
      userImageUrl={currentUser?.profileImageUrl}
      pageTitle="Product Manager"
      pageDescription="Manage all books, audio, and animations in one vendor shop page."
      navItems={navItems}
      activeNavId="products-manager"
      headerAction={
        <Link
          to="/vendor/dashboard/books"
          className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          <UploadCloud className="h-4 w-4" />
          Upload New Product
        </Link>
      }
      rightPanel={
        <div className="space-y-4">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Catalog Snapshot</h3>
            <p className="mt-2 text-2xl font-semibold text-orange-600">{productCounts.all}</p>
            <p className="mt-1 text-xs text-slate-600">Total products currently in vendor catalog.</p>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Format Breakdown</h3>
            <ul className="mt-3 space-y-2 text-xs text-slate-600">
              <li className="rounded-lg bg-orange-50 p-2">Books: {productCounts.book}</li>
              <li className="rounded-lg bg-orange-50 p-2">Audio: {productCounts.audio}</li>
              <li className="rounded-lg bg-orange-50 p-2">Animations: {productCounts.animation}</li>
            </ul>
          </article>
        </div>
      }
    >
      <div className="space-y-4 pb-2">
        <article className="rounded-2xl border border-orange-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">My Shop Products</h3>
              <p className="mt-1 text-sm text-slate-500">
                Select a type and manage what the vendor has added in books, audio, and animations.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                void refreshCatalog();
              }}
              disabled={isCatalogLoading}
              className="inline-flex items-center gap-2 rounded-xl border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <RefreshCcw className={`h-4 w-4 ${isCatalogLoading ? 'animate-spin' : ''}`} />
              {isCatalogLoading ? 'Refreshing...' : 'Refresh Products'}
            </button>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {catalogFilters.map((filter) => {
              const isActive = catalogFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setCatalogFilter(filter)}
                  className={`rounded-xl border px-3 py-2 text-left text-sm font-semibold transition ${
                    isActive
                      ? 'border-orange-500 bg-orange-500 text-white'
                      : 'border-orange-100 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <span className="block">{filterLabels[filter]}</span>
                  <span className={`text-xs ${isActive ? 'text-orange-100' : 'text-slate-500'}`}>
                    {productCounts[filter]} item{productCounts[filter] === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })}
          </div>

          <label className="mt-4 grid gap-1 text-sm">
            <span className="text-slate-600">Search products</span>
            <input
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search by title, category, or type"
              className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
            />
          </label>
          {actionFeedback.status !== 'idle' && (
            <p className={`mt-2 text-xs font-semibold ${getFeedbackTone(actionFeedback.status)}`}>
              {actionFeedback.message}
            </p>
          )}
        </article>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredProducts.length === 0 ? (
            <article className="col-span-full rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-500">
              No products found for this selection.
            </article>
          ) : (
            filteredProducts.map((product) => {
              const mediaType = getProductMediaType(product);
              return (
                <article key={product.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-white">
                  <img src={product.imageUrl} alt={t(product.nameKey)} className="h-32 w-full object-cover" />
                  <div className="space-y-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">{t(product.nameKey)}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${mediaTypeBadgeTone[mediaType]}`}>
                        {mediaTypeBadgeLabel[mediaType]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {product.categoryId
                        ? (categoryNameById.get(product.categoryId) ?? t(product.categoryKey))
                        : t(product.categoryKey)}
                    </p>
                    {editingProductId === product.id ? (
                      <div className="space-y-2 text-xs text-slate-700">
                        <label className="grid gap-1">
                          <span className="text-slate-500">Price</span>
                          <input
                            value={editPrice}
                            onChange={(event) => setEditPrice(event.target.value)}
                            className="rounded-lg border border-orange-100 px-2 py-1 focus:border-orange-400 focus:outline-none"
                          />
                        </label>
                        <label className="grid gap-1">
                          <span className="text-slate-500">Stock</span>
                          <input
                            value={editStock}
                            onChange={(event) => setEditStock(event.target.value)}
                            className="rounded-lg border border-orange-100 px-2 py-1 focus:border-orange-400 focus:outline-none"
                          />
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              void handleSaveProduct(product.id);
                            }}
                            className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingProductId(null);
                              setActionFeedback(initialFeedback);
                            }}
                            className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                          <span className="rounded-full bg-orange-50 px-2 py-1">$ {product.price.toFixed(2)}</span>
                          <span className="rounded-full bg-orange-50 px-2 py-1">{product.reviewCount} reviews</span>
                          <span className="rounded-full bg-orange-50 px-2 py-1">Stock {product.stock}</span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => beginEditProduct(product.id, product.price, product.stock)}
                            className="rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              void handleDeleteProduct(product.id);
                            }}
                            className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-orange-700"
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>
      </div>
    </DashboardFrame>
  );
};

export default VendorProductsManagerPage;
